<script setup lang="ts">
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import Trip from '@itinerary/trip/Trip';

const contentStore = useContentStore();
const {
    corporations,
    corporationTitle,
    landingDistances,
    landingPageContents,
} = classToRefs(contentStore);
const haveData = computed(
    () => !!corporations.value.length || !!landingDistances.value.length
);
</script>

<template>
    <div v-if="haveData" class="destination-info-container">
        <img
            :src="`/img/city-landings/${landingPageContents?.parent}.webp`"
            :alt="landingPageContents?.parent"
            class="image-section desktop-only"
        />
        <mr-divider
            vertical
            :thickness="2"
            color="var(--cool-gray-200)"
            class="desktop-only"
            size="312px"
        />
        <div class="destinations-content">
            <div class="content-section">
                <mr-typography
                    type="h2"
                    size="x-large"
                    weight="700"
                    color="var(--gray-800)"
                >
                    شرکت‌های {{ corporationTitle }}
                </mr-typography>
                <div class="service-providers">
                    <template v-if="landingPageContents?.parentReserveType">
                        <div
                            v-for="{ id, name } in corporations"
                            :key="id"
                            class="service-provider"
                        >
                            <NuxtImg
                                :src="
                                    Trip.getLogo(
                                        landingPageContents.parentReserveType,
                                        id
                                    )
                                "
                                class="corporation-logo"
                                :alt="name"
                            />
                            <mr-typography
                                white-space="nowrap"
                                size="small"
                                weight="500"
                                color="var(--cool-gray-900)"
                            >
                                {{ name }}
                            </mr-typography>
                        </div>
                    </template>
                </div>
            </div>
            <div class="content-section">
                <mr-typography
                    type="h2"
                    size="x-large"
                    weight="700"
                    color="var(--gray-800)"
                >
                    فاصله تا شهرهای دیگر
                </mr-typography>
                <template
                    v-for="(item, index) in landingDistances"
                    :key="item.id"
                >
                    <div v-if="(index + 1) % 2 === 0" class="cities-distance">
                        <div class="city-distance">
                            <mr-icon
                                size="medium"
                                :icon="faMapMarkerAlt"
                                color="var(--cool-gray-600)"
                            />
                            <mr-typography
                                size="small"
                                weight="500"
                                color="var(--cool-gray-900)"
                            >
                                {{ item.city }} تا
                                {{ landingPageContents?.cityName }}
                            </mr-typography>
                            <mr-typography
                                size="small"
                                weight="500"
                                color="var(--cool-gray-700)"
                            >
                                {{ item.distance }}
                            </mr-typography>
                        </div>
                        <div class="city-distance">
                            <mr-icon
                                size="medium"
                                :icon="faMapMarkerAlt"
                                color="var(--cool-gray-600)"
                            />
                            <mr-typography
                                size="small"
                                weight="500"
                                color="var(--cool-gray-900)"
                            >
                                {{ landingDistances[index - 1].city }} تا
                                {{ landingPageContents?.cityName }}
                            </mr-typography>
                            <mr-typography
                                size="small"
                                weight="500"
                                color="var(--cool-gray-700)"
                            >
                                {{ landingDistances[index - 1].distance }}
                            </mr-typography>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.destination-info-container {
    display: flex;
    flex-direction: column;
    border: 2px solid usePalette($p-cool-gray, $p-200);
    border-radius: 12px;
    padding: 12px;
    gap: 32px;
    margin-inline: 16px;
    max-width: 1200px;
    overflow: hidden;
    @include desktop-only {
        width: min(1200px, calc(100% - 32px));
        margin-inline: auto;
        flex-direction: row;
        align-items: center;
        flex-wrap: nowrap;
        gap: 64px;
        padding: 40px;
        overflow-x: scroll;
        @include no-scrollbar;
    }

    .image-section {
        width: 280px;
        height: 280px;
    }

    .divider {
        height: 100% !important;
    }

    .destinations-content {
        display: flex;
        flex-direction: column;
        gap: 32px;
        flex-grow: 1;
        @include desktop-only {
            gap: 40px;
            flex-direction: column;
        }
        .content-section {
            display: flex;
            flex-direction: column;
            gap: 12px;
            .service-providers {
                display: grid;
                grid-template-columns: repeat(3, 1fr); /* three columns */
                grid-template-rows: repeat(2, auto); /* two rows */
                gap: 6px;
                @include desktop-only {
                    display: flex;
                    align-items: center;
                    flex-shrink: 0;
                    overflow-x: scroll;
                    @include no-scrollbar;
                }
                .service-provider {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 12px;
                    border-radius: 16px;
                    background-color: usePalette($p-blue, $p-100);
                    .corporation-logo {
                        width: 24px;
                        height: 24px;
                        object-fit: contain;
                        @include desktop-only {
                            width: 32px;
                            height: 32px;
                        }
                    }
                }
            }
            .cities-distance {
                display: flex;
                flex-direction: column;
                gap: 8px;
                @include desktop-only {
                    flex-direction: row;
                }
                .city-distance {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    flex-grow: 1;
                    gap: 8px;
                    border-radius: 16px;
                    padding: 8px 12px;
                    background-color: usePalette($p-blue, $p-100);
                    overflow-x: hidden;
                }
            }
        }
    }
}
</style>
